import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Label } from "reactstrap";
import { Html5Qrcode } from "html5-qrcode";
import notifications from "../../common/Components/Notifications/Notification";
import { mediaErrorKind, mediaErrorMessage } from "../products/capture/capturePermissions";
import { parseStudioPieceId } from "./internalQr";

const READER_ID = "sales-qr-reader";
const FILE_READER_ID = "sales-qr-file-reader";

const nfcReader = () => {
  const Ctor = (window as any).NDEFReader;
  return Ctor ? new Ctor() : null;
};

const readNdef = (event: any): string => {
  const records = event?.message?.records || [];
  for (const record of records) {
    try {
      if (record.recordType === "url" || record.recordType === "text") {
        const text = new TextDecoder().decode(record.data);
        return parseStudioPieceId(text) || String(text || "").trim();
      }
    } catch {
      /* next record */
    }
  }
  return String(event?.serialNumber || "").trim();
};

/** html5-qrcode throws synchronously from stop() and clear() when the camera never started (denied, no device). */
const stopQuietly = async (scanner: Html5Qrcode) => {
  try {
    await scanner.stop();
  } catch {
    /* not running */
  }
  try {
    scanner.clear();
  } catch {
    /* nothing to clear */
  }
};

export type SalesScanner = {
  disabled?: boolean;
  cameraOn: boolean;
  nfcOn: boolean;
  stopNfc: () => void;
  startCamera: () => void;
  startNfc: () => void;
  stopCamera: () => void;
  fileRef: React.RefObject<HTMLInputElement>;
  nfcRef: React.RefObject<HTMLInputElement>;
  onFile: (file?: File) => void;
  emit: (raw: string) => void;
};

/** continuous: camera and NFC stay on after each read (phone scan sheet). */
export const useSalesScanner = (onScan: (query: string) => void, disabled?: boolean, continuous = false): SalesScanner => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const nfcRef = useRef<HTMLInputElement | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const onScanRef = useRef(onScan);
  const lastScanRef = useRef({ value: "", at: 0 });
  const liveRef = useRef(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [nfcOn, setNfcOn] = useState(false);
  const nfcAbortRef = useRef<AbortController | null>(null);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const stopNfc = () => {
    nfcAbortRef.current?.abort();
    nfcAbortRef.current = null;
    setNfcOn(false);
  };

  useEffect(() => () => nfcAbortRef.current?.abort(), []);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const stopCamera = () => {
    liveRef.current = false;
    setCameraOn(false);
  };

  const emit = (raw: string, keepCamera = false): Promise<void> => {
    const query = parseStudioPieceId(raw) || String(raw || "").trim();
    // Continuous scanning never queues a second add while one is in flight.
    if (!query || (continuous && disabledRef.current)) {
      return Promise.resolve();
    }
    if (lastScanRef.current.value === query && Date.now() - lastScanRef.current.at < 2500) {
      return Promise.resolve();
    }
    lastScanRef.current = { value: query, at: Date.now() };
    if (!continuous) {
      if (!keepCamera) stopCamera();
      setNfcOn(false);
    }
    return Promise.resolve(onScanRef.current(query));
  };

  useEffect(
    () => () => {
      liveRef.current = false;
      const scanner = scannerRef.current;
      scannerRef.current = null;
      if (scanner) {
        void stopQuietly(scanner);
      }
    },
    []
  );

  useEffect(() => {
    if (!cameraOn) {
      const scanner = scannerRef.current;
      scannerRef.current = null;
      if (scanner) {
        void stopQuietly(scanner);
      }
      return;
    }
    liveRef.current = true;
    const scanner = new Html5Qrcode(READER_ID, { verbose: false });
    scannerRef.current = scanner;
    let closed = false;
    const run = async () => {
      try {
        await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
        if (closed) {
          return;
        }
        await scanner.start(
          { facingMode: "environment" },
          { fps: 8, qrbox: { width: 220, height: 220 } },
          (text: string) => {
            if (closed || !liveRef.current) {
              return;
            }
            closed = true;
            void emit(text, true).finally(() => {
              window.setTimeout(() => { if (liveRef.current) closed = false; }, 1200);
            });
          },
          () => undefined
        );
      } catch (err) {
        if (closed) {
          return;
        }
        setCameraOn(false);
        fileRef.current?.click();
        notifications({
          title: "Scan QR",
          message: mediaErrorMessage(mediaErrorKind(err), "camera"),
          type: "info",
        });
      }
    };
    void run();
    return () => {
      closed = true;
      liveRef.current = false;
      void stopQuietly(scanner);
      if (scannerRef.current === scanner) {
        scannerRef.current = null;
      }
    };
    // emit reads onScanRef; starting is keyed off cameraOn
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOn]);

  const startCamera = () => {
    if (disabled) {
      return;
    }
    setCameraOn(true);
  };

  const onFile = async (file?: File) => {
    if (!file || disabled) {
      return;
    }
    stopCamera();
    try {
      const scanner = new Html5Qrcode(FILE_READER_ID, { verbose: false });
      const text = await scanner.scanFile(file, false);
      await scanner.clear();
      emit(String(text || ""));
    } catch {
      notifications({
        title: "Error!",
        message: "Could not read that photo. Search by name, tag, or HKP id.",
        type: "danger",
      });
    }
  };

  const startNfc = async () => {
    if (disabled) {
      return;
    }
    const reader = nfcReader();
    if (!reader) {
      setNfcOn(true);
      window.setTimeout(() => nfcRef.current?.focus(), 0);
      notifications({
        title: "Scan NFC",
        message: "This browser cannot read NFC. Type or paste the tag id.",
        type: "info",
      });
      return;
    }
    try {
      nfcAbortRef.current?.abort();
      const controller = new AbortController();
      nfcAbortRef.current = controller;
      await reader.scan({ signal: controller.signal });
      setNfcOn(true);
      reader.onreading = (event: any) => {
        const value = readNdef(event);
        if (value) {
          emit(value);
        }
      };
    } catch (err) {
      notifications({
        title: "Error!",
        message: mediaErrorMessage(mediaErrorKind(err), "camera").replace("camera", "NFC tag"),
        type: "danger",
      });
      setNfcOn(true);
      window.setTimeout(() => nfcRef.current?.focus(), 0);
    }
  };

  return {
    disabled,
    cameraOn,
    nfcOn,
    stopNfc,
    startCamera,
    startNfc,
    stopCamera,
    fileRef,
    nfcRef,
    onFile,
    emit,
  };
};

export const SalesScanIcons = ({ scan }: { scan: SalesScanner }) => (
  <div className="sales-scan-pad__actions">
    <button
      type="button"
      className={`sales-scan-pad__icon-btn${scan.cameraOn ? " is-on" : ""}`}
      disabled={scan.disabled}
      aria-label="Scan QR"
      title="Scan QR"
      onClick={scan.startCamera}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <rect x="6.5" y="6.5" width="4.5" height="4.5" rx="0.6" stroke="currentColor" strokeWidth="1.6" />
        <rect x="13" y="6.5" width="4.5" height="4.5" rx="0.6" stroke="currentColor" strokeWidth="1.6" />
        <rect x="6.5" y="13" width="4.5" height="4.5" rx="0.6" stroke="currentColor" strokeWidth="1.6" />
        <path
          d="M13 13.5h2.2v2.2H13V13.5Zm3 0h1.5V16H16v-2.5Zm-3 3h1.6V18H13v-1.5Zm3 .7h1.5V18H16v-1.3Z"
          fill="currentColor"
        />
      </svg>
    </button>
    <button
      type="button"
      className={`sales-scan-pad__icon-btn sales-scan-pad__icon-btn--nfc${scan.nfcOn ? " is-on" : ""}`}
      disabled={scan.disabled}
      aria-label="Scan NFC"
      title="Scan NFC"
      onClick={scan.startNfc}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="6" y="3.5" width="12" height="17" rx="2.2" stroke="currentColor" strokeWidth="1.7" />
        <path
          d="M10 9.2v5.6l4-5.6v5.6"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
    <input
      ref={scan.fileRef}
      type="file"
      accept="image/*"
      capture="environment"
      hidden
      onChange={(e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        scan.onFile(file);
      }}
    />
  </div>
);

export const SalesScanStage = ({ scan }: { scan: SalesScanner }) => (
  <>
    <div
      id={READER_ID}
      className={`sales-scan-pad__reader${scan.cameraOn ? "" : " is-idle"}`}
    />
    <div id={FILE_READER_ID} className="sales-scan-pad__reader is-file" />
    {scan.cameraOn ? (
      <div className="sales-scan-pad__camera">
        <Button className="btn-hastkala-outline" onClick={() => scan.fileRef.current?.click()}>
          Upload QR
        </Button>
        <Button className="btn-hastkala-outline" onClick={scan.stopCamera}>
          Stop camera
        </Button>
      </div>
    ) : null}
    {scan.nfcOn ? (
      <div className="gi-form-fields">
        <Label>NFC tag id</Label>
        <Input
          innerRef={scan.nfcRef}
          placeholder="Hold the tag, or paste xuid"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !(e.nativeEvent as KeyboardEvent).isComposing) {
              const input = e.target as HTMLInputElement;
              scan.emit(input.value);
              input.value = "";
            }
          }}
        />
      </div>
    ) : null}
  </>
);
